import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, User, Calendar, Facebook, Twitter, Linkedin, Link2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { FALLBACK_IMAGE, LOGO_URL } from "@/data/constants";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

function ShareBar({ url, title }) {
  const enc = encodeURIComponent(url);
  const encT = encodeURIComponent(title);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
  };
  const cls =
    "w-10 h-10 rounded-full border border-[#0A192F]/15 flex items-center justify-center text-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-colors";
  return (
    <div className="flex items-center gap-3" data-testid="newsletter-share-bar">
      <span className="overline text-[#4A5568]">Share</span>
      <a className={cls} data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${enc}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"><Facebook className="w-4 h-4" /></a>
      <a className={cls} data-testid="share-twitter" href={`https://twitter.com/intent/tweet?url=${enc}&text=${encT}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X"><Twitter className="w-4 h-4" /></a>
      <a className={cls} data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"><Linkedin className="w-4 h-4" /></a>
      <a className={cls} data-testid="share-email" href={`mailto:?subject=${encT}&body=${enc}`} aria-label="Share via email"><Mail className="w-4 h-4" /></a>
      <button className={cls} data-testid="share-copy" onClick={copy} aria-label="Copy link"><Link2 className="w-4 h-4" /></button>
    </div>
  );
}

export default function NewsletterDetail() {
  const { slug } = useParams();
  const [nl, setNl] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/newsletters/${slug}`).then((r) => setNl(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = typeof window !== "undefined" ? window.location.href : "";
  const jsonLd = nl
    ? [
        {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: nl.title,
          description: nl.excerpt,
          ...(nl.image_url ? { image: [nl.image_url] } : {}),
          datePublished: nl.published_at,
          dateModified: nl.published_at,
          author: { "@type": "Person", name: nl.author },
          publisher: {
            "@type": "Organization",
            name: "Wolfdog News",
            logo: { "@type": "ImageObject", url: LOGO_URL },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
            { "@type": "ListItem", position: 2, name: "Newsletter", item: origin + "/newsletter" },
            { "@type": "ListItem", position: 3, name: nl.title },
          ],
        },
      ]
    : null;
  useSeo({
    title: nl?.title,
    description: nl?.excerpt,
    image: nl?.image_url,
    type: "article",
    published: nl?.published_at,
    author: nl?.author,
    jsonLd,
  });

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold text-[#0A192F]">Newsletter not found</h1>
        <Link to="/newsletter" className="overline text-[#8B6D1F] mt-4 inline-block hover:underline">← Back to Archive</Link>
      </div>
    );
  }
  if (!nl) return <div className="max-w-3xl mx-auto px-4 py-24 text-[#4A5568]">Loading...</div>;

  const url = typeof window !== "undefined" ? window.location.href : "";

  return (
    <article className="pb-10" data-testid="newsletter-detail">
      <div className="relative h-[380px] bg-[#0A192F]">
        <img src={nl.image_url || FALLBACK_IMAGE} alt={nl.title} className="w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-3xl mx-auto px-4 pb-10">
            <span className="inline-block overline text-[#0A192F] bg-[#D4AF37] px-3 py-1 mb-4">Newsletter</span>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
              {nl.title}
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pt-8">
        <Link to="/newsletter" className="overline text-[#8B6D1F] hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to Archive
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-5 mt-5 pb-6 border-b border-[#0A192F]/10">
          <div className="flex flex-wrap items-center gap-5 text-sm text-[#4A5568]">
            <span className="flex items-center gap-2"><User className="w-4 h-4 text-[#D4AF37]" /> {nl.author}</span>
            <span className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#D4AF37]" /> {fmtDate(nl.published_at)}</span>
          </div>
          <ShareBar url={url} title={nl.title} />
        </div>

        <div className="mt-6 max-w-none">
          {(nl.content || "").split("\n").filter(Boolean).map((para, i) => {
            if (para.startsWith("@@IMG@@")) {
              const [imgUrl, caption] = para.slice(7).split("@@");
              return (
                <figure key={i} className="my-8">
                  <div className="overflow-hidden border-2 border-[#D4AF37]/50">
                    <img src={imgUrl} alt={caption || "Wolfdog"} loading="lazy" className="w-full max-h-[560px] object-cover" />
                  </div>
                  {caption && <figcaption className="mt-2 overline text-[#8B6D1F] text-center">{caption}</figcaption>}
                </figure>
              );
            }
            if (para.startsWith("@@H@@")) {
              return (
                <h2 key={i} className="font-display text-2xl font-bold tracking-tight text-[#0A192F] mt-10 mb-3">
                  {para.replace("@@H@@", "")}
                </h2>
              );
            }
            return <p key={i} className="text-lg text-[#1a2b45] leading-relaxed mb-5">{para}</p>;
          })}
        </div>

        <div className="mt-10 pt-6 border-t border-[#0A192F]/10">
          <ShareBar url={url} title={nl.title} />
        </div>
      </div>
    </article>
  );
}
